// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import addReorderPadFilters from "@adl/App/Handlers/ReorderPad/AddReorderPadFilters";
import { getReorderPadDataViewProperty } from "@adl/App/Store/Selectors/ReorderPadSelectors";
import Pagination from "@adl/MobiusOverrides/Pagination/Pagination";
import { mergeWithCss } from "@adl/XDL/Utilities";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showResultsPerPage?: boolean;
    showPagesNumber?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getReorderPadDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addReorderPadFilters,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const paginationStyles: ProductListPaginationStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                &&& {
                    margin: 0;
                }
            `,
        },
    },
};

const styles = paginationStyles;

const ReorderPadPagination = ({ addReorderPadFilters, pagination, showResultsPerPage, showPagesNumber }: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const changePage = (newPageIndex: number) => {
        addReorderPadFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addReorderPadFilters({ pageSize: newPageSize });
    };

    if (!pagination || !pagination.totalItemCount) {
        return null;
    }

    const paginationStyle = showPagesNumber
        ? styles.pagination
        : mergeWithCss(styles.pagination, {
              cssOverrides: {
                  linkList: css`
                      &&&& {
                          display: none !important;
                      }
                  `,
              },
          } as typeof styles.pagination);

    return (
        <Pagination
            {...paginationStyle}
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageOptions={showResultsPerPage ? [8, 16, 24, 32] : []}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie="ReorderPadProductList-PageSize"
            data-test-selector="productList_pagination"
            showProductsCount={showResultsPerPage}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReorderPadPagination);
